import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const SUBJECTS = ["Feedback", "Wholesale", "Press", "Catering"];
const field = "w-full bg-transparent border-b-2 border-[#181203]/40 focus:border-[#181203] outline-none py-3 text-lg placeholder:text-[#594519] transition-colors";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "Feedback", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We'll get back to you soon.");
      setForm({ name: "", email: "", subject: "Feedback", message: "" });
    } catch (err) {
      toast.error(err?.response?.status === 422 ? "Please check your email address." : "Could not send right now. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative grain bg-[#E5A910] py-24 md:py-36">
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-[#594519] mb-5">Say hello</p>
          <h2 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.04em] leading-[0.95]">
            Thirsty for <span className="font-serif-i">more?</span>
          </h2>
          <p className="mt-6 max-w-sm text-[#2B2008]">Questions, wholesale orders or a juice bar for your event — drop us a line.</p>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
            <div className="grid sm:grid-cols-2 gap-8">
              <input data-testid="contact-name-input" required placeholder="Your name" value={form.name} onChange={set("name")} className={field} />
              <input data-testid="contact-email-input" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} />
            </div>
            <div className="flex flex-wrap gap-2">
              {SUBJECTS.map((s) => (
                <button
                  type="button"
                  key={s}
                  data-testid={`contact-subject-${s.toLowerCase()}`}
                  onClick={() => setForm((f) => ({ ...f, subject: s }))}
                  className={`h-10 px-5 rounded-full border-2 border-[#181203] text-sm font-bold transition-colors ${form.subject === s ? "bg-[#181203] text-[#FFD000]" : "hover:bg-[#181203]/10"}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <textarea data-testid="contact-message-input" required rows={4} placeholder="Your message" value={form.message} onChange={set("message")} className={`${field} resize-none`} />
            <button data-testid="contact-submit-button" disabled={busy} className="group inline-flex items-center gap-3 h-14 px-8 rounded-full bg-[#181203] text-[#FFD000] font-bold hover:bg-black disabled:opacity-60 transition-colors">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              Send message
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
